/* =========================
   Angular Core
========================= */
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { DatePipe, CurrencyPipe } from '@angular/common';

/* =========================
   Third Party
========================= */
import { NgbModule, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { JwtModule } from '@auth0/angular-jwt';
import { TranslateModule, TranslateLoader, MissingTranslationHandler } from '@ngx-translate/core';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { QRCodeModule } from 'angularx-qrcode';
import { ToastrModule } from 'ngx-toastr';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../environments/environment';
/* =========================
   App Core
========================= */
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

/* =========================
   Shared / Layout
========================= */
import { SharedModule } from './shared/shared.module';
import { LayoutModule } from './modules/layouts/layout.module';
import { MomentDateFormatter } from './shared/utilities/moment-date-formatter';
import { BannerComponent } from './shared/components/banner/banner.component';

/* =========================
  Feature Modules
========================= */
import { ClubModule } from './modules/club/club.module';
import { UserModule } from './modules/user/user.module';
import { EcashModule } from './modules/ecash/ecash.module';
import { PassesModule } from './modules/passes/passes.module';

/* =========================
   FGB Core
========================= */
import {
  AuthGuard,
  PopulationGuard,
  ProductCodeGuard,
  jwtTokenGetter,
  AuthService,
  MemberQuery,
  TMOauthService,
  TMAuthService,
  tmAuthConfig,
  tmOAuthConfig,
  FGBTranslationLoader,
  FGBMissingTranslationHandler,
  MaintenanceGuard,
  DATE_OPTIONS,
  REGISTRATION_ROUTES,
  provideAppConfig,
  provideTMAuth,
} from '@fgb/core';

/* =========================
   Portal Library Modules
========================= */
import {
  BadgeAchievedModule,
  BadgeListItemModule,
  BadgeOverviewListModule,
  BadgeFeaturedListModule,
} from '@fgb/portal-component-library/src/lib/loyalty/badges';

import { FeedModule } from '@fgb/portal-component-library/src/lib/feeds';
import { FeaturedRewardsModule } from '@fgb/portal-component-library/src/lib/loyalty/featured-rewards';
import { WalletModule } from '@fgb/portal-component-library/src/lib/loyalty/wallet';
import { NotificationModule } from '@fgb/portal-component-library/src/lib/club/notification';
import { mergeRegistrationRoutes } from '@fgb/portal-component-library/src/lib/registration';
import { ScorecardModule } from '@fgb/portal-component-library/src/lib/loyalty/scorecards';
import { FaqSectionModule } from '@fgb/portal-component-library/src/lib/club/faq-section';
import { TermsAndConditionsModule } from '@fgb/portal-component-library/src/lib/club/terms-and-conditions';
import { MixedLoyaltyListModule } from '@fgb/portal-component-library/src/lib/loyalty/mixed-loyalty';
import { CartItemModule } from '@fgb/portal-component-library/src/lib/loyalty/cart-item';
import { CardLinkModule } from '@fgb/portal-component-library/src/lib/cardlinking';
import { ErrorsModule } from '@fgb/portal-component-library/src/lib/shared/errors';
import { LoadingModule } from '@fgb/portal-component-library/src/lib/shared/loading';
import { CountryAndRegionsModule } from '@fgb/portal-component-library/src/lib/shared/country-and-regions';
import { DirectivesModule } from '@fgb/portal-component-library/src/lib/directives';
import { MarketplaceListModule } from '@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-list';
import { MarketplaceDetailsModule } from '@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-details';
import { AuctionListModule } from '@fgb/portal-component-library/src/lib/loyalty/auctions/auction-list';
import { AuctionDetailsModule } from '@fgb/portal-component-library/src/lib/loyalty/auctions/auction-details';
import { LottosListModule } from '@fgb/portal-component-library/src/lib/loyalty/lottos/lottos-list';
import { LottoDetailsModule } from '@fgb/portal-component-library/src/lib/loyalty/lottos/lotto-details';
import { CardTokenListModule } from '@fgb/portal-component-library/src/lib/ecash/card-token-list';
import { PreviewModule } from '@fgb/portal-component-library/src/lib/preview';
import { EarnPointsModule } from '@fgb/portal-component-library/src/lib/club/earn-points';
import { CampaignsModule } from '@fgb/portal-component-library/src/lib/campaigns';
import { AnnouncementsModule } from '@fgb/portal-component-library/src/lib/club/announcements';
import { buildPaymentTemplates } from '@fgb/portal-component-library/src/lib/shared/payment';

/* =========================
   FEATURE: CORE PAGES
========================= */
import { MasterPageComponent } from './pages/master/master-page/master-page.component';
import { HomePageComponent } from './pages/home-page/home-page.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';

/* =========================
   FEATURE: LOGIN FLOW
========================= */
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { LoginAccountsPageComponent } from './pages/login-accounts-page/login-accounts-page.component';
import { MaintenancePageComponent } from './pages/maintenance-page/maintenance-page.component';

/* =========================
   FEATURE: TERTIARY PAGES
========================= */
import { ContactUsPageComponent } from './pages/contact-us-page/contact-us-page.component';
import { TermsPageComponent } from './pages/terms-page/terms-page.component';
import { FaqPageComponent } from './pages/faq-page/faq-page.component';
import { EarnPageComponent } from './pages/earn-page/earn-page.component';

/* =========================
   FEATURE: ACCOUNT
========================= */
import { AccountDetailsPageComponent } from './pages/features/account-section/account-details-page/account-details-page.component';
import { ActivityPageComponent } from './pages/features/account-section/account-details-page/activity-page/activity-page.component';

/* =========================
   FEATURE: REWARDS /REDEEM
========================= */
import { RewardsLayoutComponent } from './pages/features/rewards-section/rewards-layout/rewards-layout.component';
import { RewardsPageComponent } from './pages/features/rewards-section/rewards-page/rewards-page.component';
import { AuctionsPageComponent } from './pages/features/rewards-section/auctions-page/auctions-page.component';
import { MarketplacePageComponent } from './pages/features/rewards-section/marketplace-page/marketplace-page.component';
import { AuctionDetailPageComponent } from './pages/features/rewards-section/auction-detail-page/auction-detail-page.component';
import { MarketplaceDetailPageComponent } from './pages/features/rewards-section/marketplace-detail-page/marketplace-detail-page.component';
import { LottoDetailPageComponent } from './pages/features/rewards-section/lotto-detail-page/lotto-detail-page.component';
import { LottosPageComponent } from './pages/features/rewards-section/lottos-page/lottos-page.component';
import { LottosSuccessPageComponent } from './pages/features/rewards-section/lottos-page/lottos-success-page/lottos-success-page.component';
import { MarketplacePurchaseSuccessPageComponent } from './pages/features/rewards-section/marketplace-page/marketplace-purchase-success-page/marketplace-purchase-success-page.component';
import { FilterModalComponent } from './pages/features/rewards-section/rewards-page/filter-modal/filter-modal.component';

/* =========================
   FEATURE: WALLET / PASS
========================= */
import { WalletPageComponent } from './pages/features/wallet-section/wallet-page/wallet-page.component';
import { MarketplaceWalletPageComponent } from './pages/features/wallet-section/marketplace-wallet-page/marketplace-wallet-page.component';
import { WalletClaimedPageComponent } from './pages/features/wallet-section/wallet-claimed-page/wallet-claimed-page.component';
import { LottoWalletDetailsPageComponent } from './pages/features/wallet-section/lotto-wallet-details-page/lotto-wallet-details-page.component';
import { AuctionWalletDetailsPageComponent } from './pages/features/wallet-section/auction-wallet-details-page/auction-wallet-details-page.component';

/* =========================
   FEATURE: Jets Cash
========================= */
import { EcashPageComponent } from './pages/features/jets-cash-section/ecash-page/ecash-page.component';
import { EcashSuccessPageComponent } from './pages/features/jets-cash-section/ecash-page/ecash-success-page/ecash-success-page.component';
import { EcashFailurePageComponent } from './pages/features/jets-cash-section/ecash-page/ecash-failure-page/ecash-failure-page.component';
import { EcashResultPageComponent } from './pages/features/jets-cash-section/ecash-page/ecash-result-page/ecash-result-page.component';
import { EcashHistoryPageComponent } from './pages/features/jets-cash-section/ecash-history/ecash-history.component';
import { CartCheckoutPageComponent } from './pages/features/jets-cash-section/cart-checkout-page/cart-checkout-page.component';
import { CartCheckoutSuccessPageComponent } from './pages/features/jets-cash-section/cart-checkout-page/cart-checkout-success-page/cart-checkout-success-page.component';
import { CartCheckoutResultsPageComponent } from './pages/features/jets-cash-section/cart-checkout-page/cart-checkout-results-page/cart-checkout-results-page.component';
import { CartCheckoutPagePortalProductsComponent } from './pages/features/jets-cash-section/cart-checkout-page-portal-products/cart-checkout-page-portal-products.component';

/* =========================
   FEATURE: CHALLENGES
========================= */
import { BadgesPageComponent } from './pages/features/challenges-section/badges-page/badges-page.component';

/* =========================
   Registration
========================= */
import { RegistrationLayoutPageComponent } from './modules/registration/pages/registration-layout-page/registration-layout-page.component';

const tmSSOAuthConfig: tmOAuthConfig = {
  clientId: '603ad9a4785f.fortress.nyjets-jets.us',
  club: 'nyjets',
  clientSecret: 'ee89f796cb443e733729ea5cefc3cc50f942ba324b6433c2d9c9f743ff80c4c7',
  tokenUri: 'https://auth.ticketmaster.com/api/oauth/token',
  loginUri: 'https://am.ticketmaster.com/nyjets/sso',
  authoriseUri: 'https://auth.ticketmaster.com/as/authorization.oauth2',
};

const tmLoginAuthConfig: tmAuthConfig = {
  clientId: '603ad9a4785f.fortress.nyjets-jets.us',
  authUri: 'https://auth.ticketmaster.com/as/authorization.oauth2',
  loginUri: 'https://am.ticketmaster.com/nyjets/sso',
  visualPresents: 'nyjets',
};

@NgModule({
  declarations: [
    // General Pages
    AppComponent,
    MasterPageComponent,
    HomePageComponent,
    LoginPageComponent,
    LoginAccountsPageComponent,
    MaintenancePageComponent,
    TermsPageComponent,
    FaqPageComponent,
    ContactUsPageComponent,
    NotFoundPageComponent,
    // Loyalty Pages
    RewardsLayoutComponent,
    RewardsPageComponent,
    AuctionsPageComponent,
    LottosPageComponent,
    MarketplacePageComponent,
    WalletPageComponent,
    AuctionDetailPageComponent,
    LottoDetailPageComponent,
    MarketplaceDetailPageComponent,
    LottosSuccessPageComponent,
    MarketplacePurchaseSuccessPageComponent,
    LottoWalletDetailsPageComponent,
    AuctionWalletDetailsPageComponent,
    // Account Pages
    AccountDetailsPageComponent,
    ActivityPageComponent,
    // Ecash Pages
    EcashPageComponent,
    EcashResultPageComponent,
    EcashSuccessPageComponent,
    EcashFailurePageComponent,
    EcashHistoryPageComponent,

    // Club Pages
    EarnPageComponent,
    CartCheckoutPagePortalProductsComponent,
    CartCheckoutPageComponent,
    CartCheckoutSuccessPageComponent,
    CartCheckoutResultsPageComponent,
    BadgesPageComponent,
    RegistrationLayoutPageComponent,
    FilterModalComponent,
    WalletClaimedPageComponent,
    BannerComponent,
    MarketplaceWalletPageComponent,
  ],
  imports: [
    // External Modules
    RouterModule,
    CarouselModule,
    AppRoutingModule,
    FormsModule,
    NgbModule,
    BrowserModule,
    HttpClientModule,
    ReactiveFormsModule,
    InlineSVGModule.forRoot(),
    QRCodeModule,
    NotificationModule,
    BrowserAnimationsModule,
    CardLinkModule,
    ToastrModule.forRoot({
      maxOpened: 5,
    }),
    JwtModule.forRoot({
      config: {
        allowedDomains: ['http://localhost/portal2/api'],
        tokenGetter: jwtTokenGetter,
      },
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: FGBTranslationLoader,
        deps: [HttpClient, AuthService, MemberQuery],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: FGBMissingTranslationHandler,
      },
    }),
    environment.production ? [] : [],
    // Feature Modules
    LayoutModule,
    ClubModule,
    EcashModule,
    UserModule,
    SharedModule,
    PassesModule,
    BadgeListItemModule,
    BadgeAchievedModule,
    BadgeOverviewListModule,
    BadgeFeaturedListModule,
    EarnPointsModule,
    FeedModule,
    MarketplaceListModule,
    MarketplaceDetailsModule,
    AuctionDetailsModule,
    AuctionListModule,
    LottoDetailsModule,
    LottosListModule,
    CardTokenListModule,
    FeaturedRewardsModule,
    WalletModule,
    ScorecardModule,
    FaqSectionModule,
    TermsAndConditionsModule,
    CardLinkModule,
    MixedLoyaltyListModule,
    CartItemModule,
    PreviewModule,
    ErrorsModule,
    LoadingModule,
    CountryAndRegionsModule,
    CampaignsModule,
    AnnouncementsModule,

    DirectivesModule,
  ],
  providers: [
    provideAppConfig({
      dateOptions: {
        LongDateFormat: 'MMM D, yyyy h:mma',
      },
      paymentTemplates: buildPaymentTemplates(),
    }),
    provideTMAuth({
      ssoAuth: tmSSOAuthConfig,
      loginAuth: tmLoginAuthConfig,
    }),
    {
      provide: DATE_OPTIONS,
      useValue: {
        LongDateFormat: 'MMM DD, yyyy h:mma',
        ShortDateFormat: 'MMM DD, yyyy',
      },
    },
    {
      provide: REGISTRATION_ROUTES,
      useFactory: () => mergeRegistrationRoutes(),
    },
    TMOauthService,
    TMAuthService,
    AuthGuard,
    MaintenanceGuard,
    AuthService,
    PopulationGuard,
    ProductCodeGuard,
    DatePipe,
    CurrencyPipe,
    CookieService,
    { provide: NgbDateParserFormatter, useClass: MomentDateFormatter },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
